import React from 'react';
import styled from 'styled-components';
import defaultImg from 'app/components/UserProfileDropDown/assets/default-nav-icon.svg';
import { NotificationActionIcon } from 'app/assets/icons';
import { messages } from '../messages';
import { useTranslation } from 'react-i18next';
import { NotificationDto } from 'api/openapi';
import {
  dateToTimeConvertor,
  pascalCasingToSentance,
  transformText,
  useNavigateToRoute,
} from 'utils/common';
import { useNotificationApi } from 'app/hooks/useNotificationApi';

type Props = {
  data: NotificationDto;
  setIsOpenNotification: React.Dispatch<React.SetStateAction<boolean>>;
};

export const NotificationContent = ({ data, setIsOpenNotification }: Props) => {
  const { t } = useTranslation();
  const redirect = useNavigateToRoute();
  const userName = `${data?.createdBy?.firstName || ''} ${
    data?.createdBy?.lastName || ''
  }`.trim();
  //api
  const { useMarkAllRead } = useNotificationApi();
  const markAllReadMutation = useMarkAllRead({
    viewedNotifications: data.id ? [data.id] : null,
    option: null,
  });
  const formattedEntityType = data?.entityType
    ? data.entityType.toString().replace(/([a-z])([A-Z])/g, '$1 $2')
    : '';
  const handleChildImageError = event => {
    event.target.src = defaultImg;
    event.target.className = 'default';
  };
  const handleRedirect = () => {
    if (
      !data?.entityType ||
      !data?.entityId ||
      data?.notificationType?.startsWith('Deleted') ||
      data?.notificationType?.startsWith('Repaid')
    ) {
      return;
    }

    setIsOpenNotification(false);
    const paths = {
      Facility: `/facility/facility-details/${data.entityId}`,
      Loan: `/loan/loan-details/${data.entityId}`,
      Sublimit: `/facility/facility-details/${data.entityId}`,
    };
    const path = paths[data.entityType];
    if (path) {
      markAllReadMutation.mutate();
      redirect(path);
    }
  };

  return (
    <Wrapper
      className="border-bottom"
      onClick={handleRedirect}
      disabled={
        !!(
          data?.notificationType?.startsWith('Deleted') ||
          data?.notificationType?.startsWith('Repaid')
        )
      }
    >
      <ProfileWrapper className="profile-change-wrapper">
        <div style={{ width: '8px', marginRight: '6px' }}>
          <UnreadedCircleWrap
            className={data?.isRead === false ? '' : 'clear-readed'}
          />
        </div>
        <ImageWrapper>
          {data?.officialPhotoPath === null ||
          data?.officialPhotoPath === undefined ? (
            <div className="default-wrapper">
              <img src={defaultImg} alt="preview" />
            </div>
          ) : (
            <div className="default-preview-wrapper">
              <img
                src={data?.officialPhotoPath}
                alt="menu-icon"
                onError={handleChildImageError}
              />
            </div>
          )}
        </ImageWrapper>
        <div className="employee-name-time-wrapper">
          <div className="employee-name">
            {userName}
            <div className="changes-done">
              {pascalCasingToSentance(data?.notificationType ?? '')} {''}
            </div>
          </div>
          <div className="time-content-wrapper">
            <div className="time-content">
              {dateToTimeConvertor(data.createdAt ?? '')}
            </div>
            <div className="time-content" onClick={handleRedirect}>
              • {formattedEntityType}
            </div>
          </div>
        </div>
      </ProfileWrapper>
      <ContentWrapper className="content-wrapper">
        <div className="action-taken-content">
          <NotificationActionIcon />
          {t(messages.actionTaken())}
        </div>
        <div className="action-taken-details">
          {transformText(data?.description ?? '')}
        </div>
      </ContentWrapper>
    </Wrapper>
  );
};
const Wrapper = styled.div<{ disabled: boolean }>`
  cursor: ${p => (p.disabled ? 'default' : 'pointer')};
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid ${p => p.theme.notificationCntntWraper};
  .employee-name-time-wrapper {
    .time-content-wrapper {
      display: flex;
    }
    .time-content {
      font-size: 13px;
      font-weight: 400;
      line-height: 24px;
      text-align: left;
      text-underline-position: from-font;
      color: ${p => p.theme.timeContentClr};
      line-height: 10px;
      margin-left: 4px;
      margin-top: 2px;
    }
  }
`;
const ContentWrapper = styled.div`
  padding-left: 58px;
  padding-top: 10px;
  .action-taken-content {
    font-size: 13px;
    font-weight: 400;
    line-height: 24px;
    text-align: left;
    text-underline-position: from-font;
    color: ${p => p.theme.notificationCntentWrapperClr};
    display: flex;
    align-items: center;
    svg {
      margin-right: 6px;
    }
  }
  .action-taken-details {
    font-size: 13px;
    font-weight: 400;
    line-height: 24px;
    text-align: left;
    text-underline-position: from-font;
    color: ${p => p.theme.actionTakenDetailsClr};
    padding-left: 14px;
  }
`;
const ProfileWrapper = styled.div`
  display: flex;
  padding: 0 10px;
  align-items: center;
  .employee-name {
    display: flex;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    text-underline-position: from-font;
    margin-left: 5px;
    color: ${p => p.theme.userNameTextClr};
    .changes-done {
      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
      text-align: left;
      text-underline-position: from-font;
      margin-left: 5px;
    }
  }
`;
const UnreadedCircleWrap = styled.div`
  margin-right: 6px;
  border-radius: 50%;
  background: ${p => p.theme.unreadedCircleBg};
  height: 8px;
  width: 8px;
  border: 0.94px solid ${p => p.theme.white};
  &.clear-readed {
    display: none;
  }
`;

const ImageWrapper = styled.div`
  width: 28px;
  height: 28px;

  .default-preview-wrapper {
    border-radius: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${p => p.theme.navBarDefaultBgColor};
    width: 24px;
    min-width: 24px;
    height: 24px;
    object-fit: cover;
    background-size: contain;

    img {
      width: 100%;
      border-radius: 50px;
      height: 28px;
      max-height: 100%;
      &.default {
        width: 18px;
        height: 18px;
        min-height: 18px;
      }
    }
  }
`;
